import {formatCopy} from 'utils/typography';
import { reachGoal } from 'utils/metrica';
import React, {useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import 'react-phone-input-2/lib/material.css'
import get from "lodash/get";
import { useNavigate } from "react-router-dom";

import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Save as SaveIcon } from '@vedmarket/ui-kit';
import COLORS from 'styles/colors.scss';

import {
  useCreateOrderMutation,
  useGetOrder,
  useSetCompletedByOrderIdMutation,
  useSummaryCompleted,
  useUpdateOrderMutation,
} from 'api/calc'
import {PAYER_TYPE} from 'config/constants';

import Notification, {NOTIFICATION_TYPE} from '../../../../../../common/notification'
import {useDemoMainForm} from 'components/cabinet/demo/steps/main/use-main';
import {isCalculationViewModeError} from 'utils/helpers';
import {LinkPopover} from '@vedmarket/ui-kit';

import './editMainInfo.scss'

export enum ORDER_FORM {
  ORDER_NAME = 'order_name',
  COMMENT = 'comment',
  BASE_CURRENCY = 'base_currency',
  PAYER_TYPE = 'payer_type',
}

const payerLabelSx = {
  color: COLORS.BLACK_DARK,
  letterSpacing: 0,
};

const EditMainInfo = ({ onSend, hasCancelButton, onClose, onDraftNameChange } : CalculatorMainFormProps) => {

  const { t } = useTranslation()

  const demoPlayback = useDemoMainForm()

  const navigate = useNavigate()

  const { data: order } = useGetOrder()

  const [ showErrorNotification, setShowErrorNotification ] = useState<boolean | undefined>(false);

  const [ createOrder ] = useCreateOrderMutation()

  const [ updateOrder ] = useUpdateOrderMutation()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()

  const completedInfo = useSummaryCompleted();
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const defaultValues = {
    [ORDER_FORM.ORDER_NAME]: order?.[ORDER_FORM.ORDER_NAME],
    [ORDER_FORM.COMMENT]: order?.[ORDER_FORM.COMMENT],
    [ORDER_FORM.BASE_CURRENCY]: order?.[ORDER_FORM.BASE_CURRENCY],
    [ORDER_FORM.PAYER_TYPE]: order?.[ORDER_FORM.PAYER_TYPE] ?? PAYER_TYPE.INDIVIDUAL,
  };

  const { register, handleSubmit, control, reset, watch, getValues, formState: { errors, isSubmitting } } = useForm<OrderParams>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const draftName = watch(ORDER_FORM.ORDER_NAME, '');

  useEffect(() => {
    onDraftNameChange?.(draftName || '');
  }, [ draftName, onDraftNameChange ]);

  useEffect(() => () => onDraftNameChange?.(''), [ onDraftNameChange ]);

  useEffect(() => {
    if (order?.num) {
      reset(defaultValues)
    }
  }, [ order?.num, order?.payer_type ])

  const { ref: registerRef, ...registerProps } = register(ORDER_FORM.ORDER_NAME, { required: t('common__text__isRequired'), maxLength: 200 });

  const handleRef = (element: HTMLInputElement | null) => {
    registerRef(element);
    nameInputRef.current = element;
  };

  useEffect(() => {
    if (order?.num) return;

    const timeout = setTimeout(() => nameInputRef.current?.focus(), 300);
    return () => clearTimeout(timeout);
  }, [ order?.num ]);

  const onSubmit = handleSubmit(async values => {

    if (order?.num) {
      const { payer_type, ...data } = values
      const result = await updateOrder({ id: order?.num, ...data})

      if (isCalculationViewModeError(result)) {
        return;
      }

      const id = get(result, 'data.num', '')
      if (!id) {
        setShowErrorNotification(true);
        return
      }
      onSend();
      setShowErrorNotification(false);
      return
    }

    const result = await createOrder(values)
    const id = get(result, 'data.num', '')
    if (!id) {
      setShowErrorNotification(true);
      return
    }
    await setCompleted({
      orderId: id,
      ...completedInfo,
      common: true,
    })

    onSend()
    setShowErrorNotification(false);
    if (id) navigate(`${id}` )

  })

  return (
    <>
      <form
        id='main'
        className='main-form'
        onSubmit={onSubmit}
      >
        <FormControl className='main-form__customer-type' disabled={Boolean(order?.num)}>
          <Controller
            name={ORDER_FORM.PAYER_TYPE}
            control={control}
            render={({ field }) => (
              <RadioGroup {...field}>
                <FormControlLabel
                  value={PAYER_TYPE.INDIVIDUAL}
                  control={<Radio />}
                  label={
                    <Typography variant='body1' sx={payerLabelSx}>
                      {t('calc__text__main__calculationForIndividual')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value={PAYER_TYPE.LEGAL_ENTITY}
                  control={<Radio />}
                  label={
                    <Typography variant='body1' sx={payerLabelSx}>
                      {t('calc__text__main__calculationForLegalEntity')}
                    </Typography>
                  }
                />
              </RadioGroup>
            )}
          />
        </FormControl>
        <div className='main-form__field'>
          <TextField
            {...registerProps}
            inputRef={handleRef}
            inputProps={demoPlayback.fieldProps}
            fullWidth
            error={Boolean(errors[ORDER_FORM.ORDER_NAME])}
            helperText={errors[ORDER_FORM.ORDER_NAME]?.message}
            variant='outlined'
            label={t('calc__text__main__nameOfOrder')}
            InputLabelProps={{ shrink: true }}
            id={ORDER_FORM.ORDER_NAME}
          />
          <LinkPopover
            linkText={t('calc__text__main__nameHelpLink')}
            text={formatCopy(t('calc__text__main__nameHelp'))}
          />
        </div>
        <div className='main-form__field'>
          <TextField
            {...register(ORDER_FORM.COMMENT)}
            inputProps={demoPlayback.fieldProps}
            fullWidth
            variant='outlined'
            label={t('calc__text__main__comment')}
            InputLabelProps={{ shrink: true }}
            id={ORDER_FORM.COMMENT}
            multiline
            rows={2}
            InputProps={{
              sx: {
                minHeight: '72px',
              },
            }}
          />
          <LinkPopover
            linkText={t('calc__text__main__commentHelpLink')}
            text={formatCopy(t('calc__text__main__commentHelp'))}
          />
        </div>
        <div className='main-form__buttons'>
          {hasCancelButton && <Button
            variant='outlined'
            sx={{ minWidth: '212px', marginRight: 3}}
            onClick={onClose}
          >{t('actions__text__cancel')}</Button>}
          <Button
            variant='contained'
            fullWidth={!hasCancelButton}
            type='submit'
            {...demoPlayback.saveProps}
            onClick={() => reachGoal({
              goal: '1_sohranit',
              params: {
                order_name: getValues(ORDER_FORM.ORDER_NAME),
                comment: getValues(ORDER_FORM.COMMENT),
                payer_type: getValues(ORDER_FORM.PAYER_TYPE),
              },
            })}
            startIcon={<SaveIcon />}
            disabled={isSubmitting}
            sx={{ minWidth: hasCancelButton ? '404px': 'auto'}}
          >{t('actions__text__save')}</Button>
        </div>
        {showErrorNotification && <Notification type={NOTIFICATION_TYPE.ERROR} title={t('errors__message__save')} text={t('errors__message__tryLater5Min')} sx={{ marginTop: 4 }} />}
      </form>
    </>
  )
}

export default EditMainInfo
